import { z } from 'zod';
import { formatEuro } from '../format.js';
/**
 * Billing vocabulary (M8, Einstellungen 18/19/28, Onboarding Nutzer 10/15, D-07/P-37): the plans, the VAT rate, the
 * price labels, the IBAN check of the SEPA form and the request schemas of `/settings/billing` and
 * `/settings/organization/billing`. There is no payment provider: the states are data, a payment method is validated
 * and then refused (409 `PAYMENTS_NOT_ACTIVE`), no IBAN is ever stored. German strings verbatim from the screens.
 */
export const planKeys = ['free', 'team', 'single'];
export const plans = {
    // Internal plan of the demo organization (C5): no charge, seats unlimited.
    free: { key: 'free', name: 'Interner Tarif', priceNetCents: 0, features: null, note: null },
    team: { key: 'team', name: 'Team', priceNetCents: 7900, features: null, note: null },
    single: {
        key: 'single',
        name: 'Makler · Einzelplatz',
        priceNetCents: 7900,
        features: 'Deal-Pipeline, Kontaktakte, Objekte, WhatsApp-Session',
        note: 'monatlich kündbar',
    },
};
/** German VAT on the suite's invoices (E19: 79 € net → 94,01 € gross). */
export const BILLING_VAT_PCT = 19;
/** Gross amount in cents, rounded half up: 7900 → 9401. */
export function grossCents(netCents) {
    return Math.round((netCents * (100 + BILLING_VAT_PCT)) / 100);
}
/** „79 € / Monat" (E19, E28 „476,00 € / Monat" uses the exact form for totals). */
export function monthlyPriceLabel(netCents) {
    return `${formatEuro(netCents)} / Monat`;
}
/** Days a self-paying user has to add a payment method after own billing was switched on (own rule). */
export const PAYMENT_METHOD_DEADLINE_DAYS = 14;
/** Trial of an organization created through the invitation path („30 Tage testen", Onboarding Admin 01). */
export const ORGANIZATION_TRIAL_DAYS = 30;
export const PAYMENTS_NOT_ACTIVE_MESSAGE = 'Zahlungen sind in dieser Umgebung noch nicht aktiviert. Ihr Platz läuft bis dahin über die Firma.';
/** Reason of every Stripe action of E28 (disabled). */
export const PAYMENTS_NOT_ACTIVE_REASON = 'Zahlungen sind in dieser Umgebung noch nicht aktiviert.';
export const paymentMethodKeys = ['sepa', 'card', 'invoice'];
/** Tabs of the payment form (Nutzer 10). */
export const paymentMethodLabels = {
    sepa: 'SEPA-Lastschrift',
    card: 'Kreditkarte',
    invoice: 'Rechnung',
};
export const IBAN_GERMAN_LENGTH_MESSAGE = 'Eine deutsche IBAN hat 22 Stellen.';
export const IBAN_INVALID_MESSAGE = 'Die IBAN ist ungültig. Bitte prüfen Sie die Eingabe.';
/** Upper case without spaces: "de89 3704 0044 0532 0130 00" → "DE89370400440532013000". */
export function normalizeIban(raw) {
    return raw.replace(/\s+/g, '').toUpperCase();
}
/** ISO 13616 check digits (mod 97 = 1). */
function ibanChecksumOk(iban) {
    const rearranged = `${iban.slice(4)}${iban.slice(0, 4)}`;
    let remainder = 0;
    for (const char of rearranged) {
        const code = char.charCodeAt(0);
        const digits = code >= 65 && code <= 90 ? String(code - 55) : char;
        for (const digit of digits)
            remainder = (remainder * 10 + Number(digit)) % 97;
    }
    return remainder === 1;
}
/** German error text of an IBAN, or null when it is valid (DE length 22 first, then format and mod 97). */
export function ibanError(raw) {
    const iban = normalizeIban(raw);
    if (iban.startsWith('DE') && iban.length !== 22)
        return IBAN_GERMAN_LENGTH_MESSAGE;
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban))
        return IBAN_INVALID_MESSAGE;
    return ibanChecksumOk(iban) ? null : IBAN_INVALID_MESSAGE;
}
export const MANDATE_REQUIRED_MESSAGE = 'Bitte erteilen Sie das SEPA-Lastschriftmandat.';
/** `POST /settings/billing/payment-method` — validated completely, then refused; nothing is stored. */
export const paymentMethodBodySchema = z
    .object({
    method: z.enum(paymentMethodKeys),
    holder: z.string().trim().max(200).default(''),
    iban: z.string().trim().max(64).default(''),
    address: z.string().trim().max(500).default(''),
    vatId: z.string().trim().max(32).nullable().optional(),
    mandate: z.boolean().default(false),
})
    .superRefine((body, ctx) => {
    if (body.address.length === 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['address'],
            message: 'Bitte geben Sie eine Rechnungsanschrift an.',
        });
    }
    if (body.method !== 'sepa')
        return;
    if (body.holder.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['holder'], message: 'Bitte geben Sie den Kontoinhaber an.' });
    }
    const error = ibanError(body.iban);
    if (error !== null)
        ctx.addIssue({ code: 'custom', path: ['iban'], message: error });
    if (!body.mandate)
        ctx.addIssue({ code: 'custom', path: ['mandate'], message: MANDATE_REQUIRED_MESSAGE });
});
/** `PUT /settings/billing/address` (E19 „Rechnungsanschrift" · „Ändern"); lines separated by "\n". */
export const billingAddressBodySchema = z.object({
    address: z.string().trim().min(1, 'Bitte geben Sie eine Rechnungsanschrift an.').max(500),
    vatId: z.string().trim().max(32).nullable().optional(),
});
/** `PUT /settings/organization/billing/invoice-email` (E28 „Rechnungsempfang" · „Ändern"). */
export const invoiceEmailBodySchema = z.object({
    email: z.string().trim().toLowerCase().email('Bitte eine gültige E-Mail-Adresse angeben.').max(254),
});
export const subscriptionStatusKeys = ['free', 'trial', 'active', 'dunned', 'cancelled'];
/** Invoice status pills (E19/E28). */
export const invoiceStatusLabels = {
    open: 'Offen',
    paid: 'Bezahlt',
    chargeback: 'Rückbuchung',
    cancelled: 'Storniert',
};
//# sourceMappingURL=billing.js.map